<script setup lang="ts">
import NavItem from '@/components/navigation/NavItem.vue';
import PickOrganizationMenu from '@/components/organizations/PickOrganizationMenu.vue';
import { Feature } from '@/enums/feature';
import { useAuthStore } from '@/stores/auth';
import { useModuleStore } from '@/stores/module';
import { navIcons } from '@/utils/nav';
import { lowercaseFirstLetter } from '@/utils/text';
import toast from '@/utils/toast';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';

const authStore = useAuthStore();
const moduleStore = useModuleStore();

const isOrganizationMenuOpen = ref(false);
const isModulesMenuOpen = ref(false);
const employeesMayViewDocuments = ref(false);

const router = useRouter();
const route = useRoute();

const { smAndDown, mdAndDown, lgAndUp } = useDisplay();

const isMenuOpen = ref(false);

function detectMenuOpen() {
    isMenuOpen.value = lgAndUp.value;
}

onMounted(() => {
    detectMenuOpen();
    window.addEventListener('resize', detectMenuOpen);
});

onUnmounted(() => {
    window.removeEventListener('resize', detectMenuOpen);
});

watch(
    () => route.path,
    () => {
        if (!lgAndUp.value) isMenuOpen.value = false;
        if (route.name == 'manual' && !smAndDown.value) isMenuOpen.value = true;
    }
);

async function logout(): Promise<void> {
    authStore.logout();
    await router.push('/login');
    toast.success('LoggedOut');
}

function closeOrganizationMenu(): void {
    isOrganizationMenuOpen.value = false;
}

function closeModulesMenu(): void {
    isModulesMenuOpen.value = false;
}

async function fetchModules() {
    if (!authStore.currentOrganization) return;
    await moduleStore.get();
}

async function checkEmployeesMayViewDocuments() {
    employeesMayViewDocuments.value = await authStore.rootOrganizationHasFeature(Feature.EmployeesMayViewDocuments);
}

watch(
    () => authStore.currentOrganization,
    () => {
        fetchModules();
        checkEmployeesMayViewDocuments();
    },
    { immediate: true }
);
</script>

<template>
    <div class="hamburger-icon" @click="isMenuOpen = !isMenuOpen" v-show="mdAndDown">
        <v-icon icon="menu" :class="['text-white', !isMenuOpen && !smAndDown ? 'bg-main' : '']" />
    </div>
    <v-navigation-drawer :permanent="lgAndUp" :temporary="!lgAndUp" v-model="isMenuOpen" :width="smAndDown ? '100%' : 215" class="shadow-lg border-0">
        <template #prepend>
            <v-row :class="['justify-center', 'align-center', 'bg-main-dark']" v-if="!smAndDown">
                <router-link
                    v-if="authStore.isAuthenticated"
                    :to="authStore.currentOrganization ? '/dashboard' : `/medewerker/${authStore.getLoggedInEmployee()._id}`"
                    class="d-flex justify-center align-center"
                >
                    <img src="/images/logo.svg" alt="logo" :height="105" :class="[mdAndDown ? 'mt-7' : '']" />
                </router-link>
            </v-row>
            <PickOrganizationMenu class="overflow-hidden" v-if="authStore.areMultipleOrganizationsAccessible" />

            <NavItem v-if="!authStore.currentOrganization" route="/bedrijf-selecteren" displayName="Bedrijf selecteren" prefixIcon="dynamic_feed" />

            <NavItem
                v-if="authStore.isAuthenticated"
                id="profileNavItem"
                routeName="employee"
                :routeParams="{ id: authStore.getLoggedInEmployee()._id }"
                :displayName="authStore.getLoggedInEmployee().firstName"
                :prefixIcon="navIcons.profile"
            />
        </template>
        <div>
            <div class="mt-5" v-if="authStore.currentOrganization">
                <NavItem
                    v-if="authStore.currentOrganization"
                    id="dashboardNavItem"
                    routeName="dashboard"
                    :routeReplace="true"
                    displayName="Dashboard"
                    :prefixIcon="navIcons.dashboard"
                    prefixIconClass="text-alt"
                />

                <NavItem id="actionListNavItem" routeName="actionPoints" displayName="Actielijst" :prefixIcon="navIcons.actionPoints" />

                <div v-if="authStore.isAdminOrActionCoordinator || employeesMayViewDocuments">
                    <v-menu location="right">
                        <template v-slot:activator="{ props }">
                            <v-list-item>
                                <NavItem
                                    v-bind="props"
                                    id="modulesNavItem"
                                    @click="isModulesMenuOpen = true"
                                    displayName="Modules"
                                    :prefixIcon="navIcons.modules"
                                    :suffixIcon="isModulesMenuOpen ? 'chevron_left' : 'chevron_right'"
                                />
                            </v-list-item>
                        </template>

                        <v-list class="navigation-submenus">
                            <v-list-item v-click-outside="closeModulesMenu" @click="closeModulesMenu">
                                <NavItem routeName="modules" displayName="Overzicht" />
                            </v-list-item>

                            <v-list-item v-for="module in moduleStore.modules" :key="module._id">
                                <NavItem routeName="module" :routeParams="{ module: module.name }" :displayName="module.name" />
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </div>

            <div class="mt-5" v-if="authStore.currentOrganization">
                <v-menu location="right" v-if="authStore.isAdmin">
                    <template v-slot:activator="{ props }">
                        <v-list-item>
                            <NavItem
                                id="orgNavItem"
                                v-bind="props"
                                @click="isOrganizationMenuOpen = true"
                                :displayName="`Mijn ${lowercaseFirstLetter(authStore.currentOrganization.type)}`"
                                :prefixIcon="navIcons.myOrganization"
                                :suffixIcon="isOrganizationMenuOpen ? 'chevron_left' : 'chevron_right'"
                            />
                        </v-list-item>
                    </template>
                    <v-list class="navigation-submenus">
                        <v-list-item v-click-outside="closeOrganizationMenu" @click="closeOrganizationMenu">
                            <NavItem
                                routeName="organization-profile"
                                :routeParams="{ organizationId: authStore.currentOrganization._id }"
                                displayName="Informatie"
                            />
                            <NavItem
                                routeName="organization-underlying"
                                :routeParams="{ organizationId: authStore.currentOrganization._id }"
                                displayName="Vestigingen"
                            />
                            <NavItem
                                routeName="employees"
                                :routeParams="{ organizationId: authStore.currentOrganization._id }"
                                displayName="Medewerkers"
                            />
                        </v-list-item>
                    </v-list>
                </v-menu>

                <NavItem
                    v-if="!authStore.isActionCoordinator"
                    id="teamsNavItem"
                    :classes="route.name === 'my-teams' || route.name === 'admin-teams' ? 'active' : ''"
                    :routeName="authStore.isAdminOrActionCoordinator ? 'admin-teams' : 'my-teams'"
                    :routeParams="!authStore.isAdminOrActionCoordinator ? { id: authStore.getLoggedInEmployee()._id } : {}"
                    :displayName="authStore.isAdminOrActionCoordinator ? 'Teams' : 'Mijn teams'"
                    :prefixIcon="navIcons.teams"
                />
            </div>

            <NavItem
                routeName="superadmin"
                displayName="Hoofdbeheer"
                :prefixIcon="navIcons.admin"
                prefixIconClass="text-alt"
                v-if="authStore.isSuperAdmin()"
                class="mt-10"
            />
            <NavItem v-if="authStore.currentOrganization && authStore.isAdmin" routeName="admin" displayName="Beheer" :prefixIcon="navIcons.admin" />
            <NavItem
                v-if="authStore.currentOrganization"
                :prefixIcon="navIcons.manual"
                routeName="manual"
                displayName="Handleiding"
                @open-menu="isMenuOpen = true"
            />
        </div>

        <template #append>
            <NavItem displayName="Uitloggen" :prefixIcon="navIcons.logout" @click="logout" />
        </template>
    </v-navigation-drawer>
</template>

<style scoped>
li {
    list-style: none;
}

a.router-link-exact-active {
    color: var(--color-text);
}

a:hover {
    background-color: transparent;
    cursor: pointer;
}

.sub-menu {
    background-color: var(--color-main);
    color: var(--color-text-white);
    margin-top: 0px;
}

.sub-menu a {
    background-color: var(--c-main-light);
    color: var(--vt-c-white);
    &:hover {
        background-color: var(--c-main-dark);
    }
}

html #app .custom-button {
    background-color: green !important;
    &:hover {
        background-color: pink !important;
    }
}

a {
    padding: 15px;
    color: var(--color-text);
    text-decoration: none;
    display: block;
    border-left: 3px solid transparent;
    &:hover {
        background-color: var(--c-main-dark);
        color: white;
    }
}

.hamburger-icon {
    cursor: pointer;
    user-select: none;
    position: fixed;
    top: 20px;
    left: 20px;
    z-index: 9999;
}

.hamburger-icon i {
    font-size: 30px;
}

nav:deep() .v-list-item__append {
    display: none !important;
}

.active {
    background-color: var(--c-main-dark);
    color: white !important;
}

.v-list-item {
    width: 100% !important;
    padding: 0 !important;
}

:deep(.v-list-item__content, .v-list-item) {
    z-index: 9999999 !important;
}
.navigation-submenus {
    width: 225px !important;
    padding: 0;

    a:first-of-type {
        border-top: 1px solid var(--c-grey);
    }

    a {
        border: 1px solid var(--c-grey);
        border-top: none;
    }
}
</style>
